// =====================================================================
// crmCustomer — Opportunity Close tab
//
// RiskRegisterEditor — per-deal risk list (MEDDPICCR R element).
//
// R goes ON only when the register has at least one row and every `open`
// row has both owner and mitigation filled in. The editor also surfaces
// "implied" baseline risks (single-threaded, no compelling event) as
// chips with an "Add to register" button — the spec asks for auto-seed,
// but we only persist on explicit user confirmation to avoid silent
// writes (see deviations doc D-1.2.5).
// =====================================================================

const RISK_STATUSES = ["open", "mitigated", "accepted"];

function deriveImpliedRisks(opp, stakeholders) {
  const out = [];
  const nonPrimaryStakeholders = (stakeholders || []).filter(s => s && !s.isPrimary);
  if (nonPrimaryStakeholders.length === 0) {
    out.push({
      id: "risk_" + opp.id + "_singlethreaded",
      label: "Single-threaded — primary contact is the only engaged stakeholder.",
      riskText: "Single-threaded deal: primary contact is the only engaged stakeholder on this opportunity.",
    });
  }
  if (!opp.compellingEvent) {
    out.push({
      id: "risk_" + opp.id + "_no_event",
      label: "No compelling event identified on the deal.",
      riskText: "No compelling event has been identified — deal lacks a forcing function for close-by date.",
    });
  }
  return out;
}

function RiskRow({ row, onSave, onRemove, busy }) {
  const [draft, setDraft] = useState(row);
  useEffect(() => { setDraft(row); }, [row.id]);
  const dirty = JSON.stringify(draft) !== JSON.stringify(row);
  return (
    <tr style={{ cursor: "default", opacity: busy ? 0.5 : 1 }}>
      <td>
        <input type="text" value={draft.risk || ""} onChange={e => setDraft(d => ({ ...d, risk: e.target.value }))}
          style={{ width: "100%" }} placeholder="What's the risk?" />
      </td>
      <td>
        <input type="text" value={draft.owner || ""} onChange={e => setDraft(d => ({ ...d, owner: e.target.value }))}
          style={{ width: "100%" }} placeholder="Who owns it?" />
      </td>
      <td>
        <input type="text" value={draft.mitigation || ""} onChange={e => setDraft(d => ({ ...d, mitigation: e.target.value }))}
          style={{ width: "100%" }} placeholder="Mitigation plan" />
      </td>
      <td>
        <select value={draft.status || "open"} onChange={e => setDraft(d => ({ ...d, status: e.target.value }))}>
          {RISK_STATUSES.map(s => <option key={s}>{s}</option>)}
        </select>
      </td>
      <td className="right">
        {dirty && <button className="btn btn-sm" onClick={() => onSave(draft)} disabled={busy}>Save</button>}
        <button className="btn btn-sm" onClick={() => onRemove(row.id)} disabled={busy} style={{ marginLeft: 4 }}>Remove</button>
      </td>
    </tr>
  );
}

function RiskRegisterEditor({ opp, stakeholders }) {
  const rows = (D.OPPORTUNITY_RISKS || []).filter(r => r.oppId === opp.id);
  const implied = deriveImpliedRisks(opp, stakeholders);
  const impliedNotYetAdded = implied.filter(i => !rows.some(r => r.id === i.id));
  const [busyId, setBusyId] = useState(null);

  async function saveRow(row) {
    setBusyId(row.id);
    try {
      // Upsert: insert if id is new, update otherwise. Since the editor
      // assigns ids on create, we always know which we want.
      const existing = rows.find(r => r.id === row.id);
      if (existing) {
        await tpAPI.opportunityRisks.update(row.id, row);
      } else {
        await tpAPI.opportunityRisks.create(row);
      }
    } catch (e) { console.error("Failed to save risk:", e); }
    finally { setBusyId(null); }
  }

  async function removeRow(id) {
    setBusyId(id);
    try { await tpAPI.opportunityRisks.remove(id); }
    catch (e) { console.error("Failed to remove risk:", e); }
    finally { setBusyId(null); }
  }

  async function addBlank() {
    const id = "risk_" + opp.id + "_" + Date.now();
    await saveRow({ id, oppId: opp.id, risk: "", owner: "", mitigation: "", status: "open" });
  }

  async function adoptImplied(suggestion) {
    await saveRow({
      id: suggestion.id, oppId: opp.id, risk: suggestion.riskText,
      owner: "", mitigation: "", status: "open",
    });
  }

  return (
    <div className="card" style={{ marginTop: 12 }}>
      <div className="card-head" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
        <div>
          <div className="card-title">Risk register (MEDDPICCR · R)</div>
          <div className="card-sub">R lights up when every open risk has both owner and mitigation filled in.</div>
        </div>
        <button className="btn btn-sm" onClick={addBlank}>+ Add risk</button>
      </div>
      <div className="card-body">
        {rows.length === 0 ? (
          <div className="muted" style={{ fontSize: 13, marginBottom: impliedNotYetAdded.length ? 12 : 0 }}>
            No risks logged yet — R is OFF until at least one risk is in the register and every open row has owner + mitigation.
          </div>
        ) : (
          <table className="tbl tbl-flush">
            <thead><tr>
              <th>Risk</th><th>Owner</th><th>Mitigation</th><th>Status</th><th></th>
            </tr></thead>
            <tbody>
              {rows.map(r => (
                <RiskRow key={r.id} row={r} onSave={saveRow} onRemove={removeRow} busy={busyId === r.id} />
              ))}
            </tbody>
          </table>
        )}
        {impliedNotYetAdded.length > 0 && (
          <div style={{ marginTop: 12, padding: "8px 12px", background: "var(--amber-soft, #fef3c7)", borderRadius: 6 }}>
            <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 6 }}>Implied risks for this deal — not yet added:</div>
            {impliedNotYetAdded.map(s => (
              <div key={s.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, padding: "4px 0", fontSize: 12.5 }}>
                <span>· {s.label}</span>
                <button className="btn btn-sm" onClick={() => adoptImplied(s)}>Add to register</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

window.RISK_STATUSES = RISK_STATUSES;
window.deriveImpliedRisks = deriveImpliedRisks;
window.RiskRow = RiskRow;
window.RiskRegisterEditor = RiskRegisterEditor;
